import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router'
import { PageHeader } from '~/components/PageHeader'
import { TabNav, type TabItem } from '~/components/TabNav'
import { ADMIN_NAV, navNodeTarget, resolveActiveAdminNav } from '~/lib/admin-nav'
import { usePermissionGuard } from '~/lib/current-user'
import { seo } from '~/utils/seo'

export const Route = createFileRoute('/admin')({
  head: () => ({ meta: seo({ title: 'Admin' }) }),
  component: AdminLayout,
})

function AdminLayout() {
  // Non-admins bounce back to home; server functions also refuse them.
  const isAdmin = usePermissionGuard('admin')
  const matches = useMatches()

  if (!isAdmin) {
    return (
      <div className="text-muted-foreground text-sm">Checking permissions...</div>
    )
  }

  const activePaths: string[] = matches.map((m) => m.fullPath)
  const active = resolveActiveAdminNav(activePaths)

  const topTabs: TabItem[] = ADMIN_NAV.map((node) => ({
    to: navNodeTarget(node),
    label: node.label,
    isActive: active?.node === node,
  }))
  const subTabs: TabItem[] = (active?.subTabs ?? []).map((tab) => ({
    to: tab.to,
    label: tab.label,
    isActive: activePaths.includes(tab.to),
  }))

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin"
        description="Manage users, permissions, and external integrations"
      />
      <TabNav tabs={topTabs} ariaLabel="Admin sections" />
      {subTabs.length > 0 && <TabNav tabs={subTabs} ariaLabel="Admin subsections" />}
      <Outlet />
    </div>
  )
}
